import { useState, useEffect } from "react";
import { useAuth } from "../../auth";
import { wrapperRequests } from "../../../services/api";
import { routesURL } from "../../../services/routesUrl";

export interface AutoPrintData {
  id: string;
  name: string;
  createdAt: string;
  status: "IN_PROGRESS" | "STANDING_BY" | "COMPLETED" | "ERROR" | "CANCELED";
}

export interface AutoPrintResponse {
  content: AutoPrintData[];
  page: number;
  totalDocuments: number;
  totalPages: number;
}

interface UseAutoPrintParams {
  page?: number;
  limit?: number;
  search?: string;
}

export function useAutoPrint() {
  const [autoPrintData, setAutoPrintData] = useState<AutoPrintData[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    rowsPerPage: 50,
    totalDocuments: 0,
    totalPages: 0,
  });
  const [search, setSearch] = useState("");

  const { user } = useAuth();

  const fetchAutoPrint = async (params: UseAutoPrintParams = {}) => {
    if (!user?.user?.id) return;

    setLoading(true);
    try {
      const response = await wrapperRequests<AutoPrintResponse>(
        routesURL.inventory.autoprint.getAutoPrint({
          userId: user.user.id,
          page: params.page || pagination.currentPage,
          limit: params.limit || pagination.rowsPerPage,
          search: params.search !== undefined ? params.search : search,
        }),
        "GET",
      );

      setAutoPrintData(response.data.content || []);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.data.page || 1,
        totalDocuments: response.data.totalDocuments || 0,
        totalPages: response.data.totalPages || 0,
      }));
    } catch (error) {
      console.error("Error fetching auto print data.", error);
      setAutoPrintData([]);
      setPagination((prev) => ({
        ...prev,
        totalDocuments: 0,
        totalPages: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchAutoPrint({ page: 1, search: searchTerm });
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchAutoPrint({ page, search });
  };

  const handleRowsPerPageChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, rowsPerPage: limit, currentPage: 1 }));
    fetchAutoPrint({ page: 1, limit, search });
  };

  useEffect(() => {
    if (user?.user?.id) {
      fetchAutoPrint();
    }
  }, [user?.user?.id]);

  return {
    autoPrintData,
    loading,
    pagination,
    search,
    fetchAutoPrint,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  };
}
